import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { toCitySlug } from "@/lib/citySlug";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const DEFAULT_TIMEZONE = "Asia/Kolkata";
const BASE_URL = (process.env.NEXT_PUBLIC_CUSTOMER_URL || "https://tykkit.com").replace(/\/+$/, "");

/**
 * Returns a valid ISO 8601 string with timezone offset (e.g. 2026-09-13T20:30:00+05:30)
 * for a real date/time. Callers must guarantee `date` is present — this does
 * not invent a date when one wasn't provided.
 */
export const getISODateTime = (date, time, tz) => {
  const targetTz = tz && typeof tz === "string" ? tz : DEFAULT_TIMEZONE;
  const timeStr = time && typeof time === "string" && time.trim() ? time.trim() : "00:00:00";

  try {
    let parsed = dayjs.tz(
      `${date} ${timeStr}`,
      [
        "YYYY-MM-DD HH:mm:ss",
        "YYYY-MM-DD HH:mm",
        "YYYY-MM-DD",
        "DD-MM-YYYY HH:mm:ss",
        "DD-MM-YYYY",
        "DD MMM YYYY",
        "YYYY/MM/DD",
      ],
      targetTz,
    );

    if (!parsed.isValid()) {
      parsed = dayjs.tz(date, targetTz);
    }
    if (!parsed.isValid()) {
      parsed = dayjs(`${date} ${timeStr}`);
    }

    return parsed.isValid() ? parsed.format() : null;
  } catch {
    return null;
  }
};

const EVENT_STATUS_MAP = {
  cancelled: "EventCancelled",
  canceled: "EventCancelled",
  postponed: "EventPostponed",
  rescheduled: "EventRescheduled",
  moved_online: "EventMovedOnline",
  online: "EventMovedOnline",
  completed: "EventCompleted",
  past: "EventCompleted",
  closed: "EventCompleted",
  ended: "EventCompleted",
};

export const mapEventStatus = (status, endDate) => {
  const normalized = String(status || "").toLowerCase().trim();
  if (EVENT_STATUS_MAP[normalized]) {
    return EVENT_STATUS_MAP[normalized];
  }
  if (endDate) {
    try {
      const end = new Date(endDate);
      if (!Number.isNaN(end.getTime()) && end < new Date()) {
        return "EventCompleted";
      }
    } catch {
      // ignore
    }
  }
  return "EventScheduled";
};

/**
 * Resolves a real, absolute image URL for the event. Returns null when no
 * genuine event image exists — callers must omit the `image` field rather
 * than substituting a placeholder/logo, which would misrepresent the event.
 */
const resolveAbsoluteImageUrl = (image, feature_images) => {
  const extractUrl = (item) => {
    if (!item) return null;
    if (typeof item === "string" && item.trim()) return item.trim();
    if (item?.url && typeof item.url === "string") return item.url.trim();
    if (item?.src && typeof item.src === "string") return item.src.trim();
    if (item?.thumbnail && typeof item.thumbnail === "string") return item.thumbnail.trim();
    if (Array.isArray(item) && item.length > 0) return extractUrl(item[0]);
    return null;
  };

  const raw = extractUrl(image) || extractUrl(feature_images);
  if (!raw) return null;
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  return `${BASE_URL}${raw.startsWith("/") ? "" : "/"}${raw}`;
};

/**
 * Extracts a numeric price from string / number. Returns null when there is
 * no real price value at all (as opposed to a genuine 0 for a free event),
 * so callers can tell "free" apart from "no pricing data".
 */
const parsePrice = (priceVal) => {
  if (priceVal === 0 || priceVal === "0" || priceVal === "FREE" || priceVal === "free") {
    return 0;
  }
  if (priceVal === null || priceVal === undefined || priceVal === "") return null;
  const str = String(priceVal).replace(/[^0-9.]/g, "");
  if (!str) return null;
  const num = parseFloat(str);
  return Number.isFinite(num) ? num : null;
};

/**
 * Resolves the single canonical path for an event, regardless of which URL
 * shape it was reached through (bare `/event/{slug}`, legacy `/events/{slug}`,
 * or a mismatched/missing city prefix). Every one of those alternate paths
 * should point at the *same* canonical URL — this is what lets Google
 * consolidate them as one page instead of flagging "duplicate content
 * without a user-selected canonical", which was previously happening
 * because each URL shape self-referenced its own requested path as
 * canonical instead of a single agreed-upon one.
 */
export const getEventCanonicalPath = (slugOrPath, city) => {
  const clean = String(slugOrPath || "").replace(/^\/+|\/+$/g, "");
  if (!clean) return null;
  if (clean.startsWith("http://") || clean.startsWith("https://")) return clean;

  // Strip any "event/"/"events/" segment (with or without a city prefix)
  // down to the bare event slug — the one piece of the path that's actually
  // unique per event.
  // Split at the LAST marker: matching the first one turned a path that
  // already repeated "{city}/event/" into a canonical of
  // "/uşak/event/u-ak/event/admin-event-1-cme4xr".
  const segments = clean.split("/");
  let markerIndex = -1;
  segments.forEach((segment, i) => {
    if (/^events?$/i.test(segment)) markerIndex = i;
  });
  const bareSlug = (
    markerIndex >= 0
      ? segments.slice(markerIndex + 1).join("/")
      : segments[segments.length - 1] || ""
  ).trim();
  if (!bareSlug) return null;

  // A "{city}/event/{slug}" path already names the city exactly as the URL
  // spells it, so it wins over re-deriving one from the venue's display name —
  // "Uşak" slugifies to "uşak", but the event's real URL is "/u-ak/event/...".
  const pathCity =
    markerIndex === 1 && /^[a-z0-9-]+$/i.test(segments[0])
      ? segments[0].toLowerCase()
      : "";

  // The caller passes `event.venue.city`, which the API returns in display
  // form ("Ahmedabad"). Interpolated raw, that produced a second crawlable
  // URL casing for a page that already exists in lowercase — and GSC reported
  // exactly that as a canonical mismatch on event pages.
  const citySlug = pathCity || toCitySlug(city, { fallback: "" });

  const lowerBareSlug = bareSlug.toLowerCase();
  return citySlug ? `/${citySlug}/event/${lowerBareSlug}` : `/events/${lowerBareSlug}`;
};

/**
 * Resolves full canonical URL for an event
 */
const getCanonicalEventUrl = (slug, city) => {
  const path = getEventCanonicalPath(slug, city);
  if (!path) return `${BASE_URL}/explore/${toCitySlug(city)}/events`;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BASE_URL}${path}`;
};

/**
 * Resolves the single canonical path for an artist, regardless of which URL
 * shape it was reached through (bare `/artist/{slug}` or other variants).
 * This ensures all URL variants point to the same canonical URL for proper
 * Google consolidation.
 */
export const getArtistCanonicalPath = (slugOrPath) => {
  const clean = String(slugOrPath || "").replace(/^\/+|\/+$/g, "");
  if (!clean) return null;
  if (clean.startsWith("http://") || clean.startsWith("https://")) return clean;

  // Strip all "artist/" segments down to the bare artist slug (fixes DEV-017)
  const bareSlug = clean.replace(/^(?:.*\/)?artist\//i, "").replace(/^(?:artist\/)+/i, "").trim();
  if (!bareSlug) return null;

  return `/artist/${bareSlug}`;
};

/**
 * Resolves full canonical URL for an artist
 */
export const getCanonicalArtistUrl = (slug) => {
  const path = getArtistCanonicalPath(slug);
  if (!path) return `${BASE_URL}/artists`;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BASE_URL}${path}`;
};

/**
 * Resolves PostalAddress and Place for location using only fields the venue
 * actually has. Never invents a city/state/postal code — a partial or
 * missing address is represented as a partial or absent `address` object,
 * not padded out with fabricated defaults.
 *
 * A "yet to be announced" venue still knows its city: the API stores the city
 * itself as the venue (name/line1 "Ahmedabad", line2 "Gujarat, India", geo at
 * the city centre). Those city-level fields are real and are what Google
 * needs for `address`, so they're kept. Everything below city level is a
 * placeholder and is left out.
 */
const resolveLocation = (venue, venueTbd) => {
  const isString = typeof venue === "string";
  const venueName = venueTbd
    ? "Venue to be announced"
    : isString
      ? venue.trim() || "Venue to be announced"
      : venue?.name || venue?.city || "Venue to be announced";

  const place = {
    "@type": "Place",
    name: venueName,
  };

  if (venueTbd && !isString && venue) {
    const address = {};
    if (venue.city) address.addressLocality = venue.city;
    if (venue.state) address.addressRegion = venue.state;
    if (venue.country) address.addressCountry = venue.country;

    if (Object.keys(address).length > 0) {
      place.address = { "@type": "PostalAddress", ...address };
    }
  }

  if (!venueTbd && !isString && venue) {
    const street = venue.line1
      ? `${venue.line1}${venue.line2 ? `, ${venue.line2}` : ""}`.trim()
      : venue.line2?.trim();

    const address = {};
    if (street) address.streetAddress = street;
    if (venue.city) address.addressLocality = venue.city;
    if (venue.state) address.addressRegion = venue.state;
    if (venue.pincode) address.postalCode = venue.pincode;
    if (venue.country) address.addressCountry = venue.country;

    if (Object.keys(address).length > 0) {
      place.address = { "@type": "PostalAddress", ...address };
    }

    if (venue.lat && (venue.lng || venue.long)) {
      place.geo = {
        "@type": "GeoCoordinates",
        latitude: Number(venue.lat),
        longitude: Number(venue.lng || venue.long),
      };
    }
  }

  return place;
};

/**
 * Resolves organizer only when real organizer data exists. Returns
 * undefined otherwise — Tykkit is the ticketing platform, not the event's
 * organizer, so it must never be used as a default.
 */
const resolveOrganizer = (organizer, organizerSlug) => {
  if (!organizer) return undefined;

  // The organizer's Tykkit profile (/organizer/<workspace slug>) is the
  // fallback url — the event API sends the name as a bare string with the
  // slug alongside, and Google flags an organizer without a url.
  const slug = typeof organizerSlug === "string" ? organizerSlug.trim() : "";
  const profileUrl = slug
    ? `${BASE_URL}/organizer/${slug.replace(/^\/*(organizer\/)?/, "")}`
    : undefined;

  if (typeof organizer === "string") {
    const name = organizer.trim();
    return name
      ? {
          "@type": "Organization",
          name,
          ...(profileUrl ? { url: profileUrl } : {}),
        }
      : undefined;
  }

  const name = organizer.name || organizer.organizer_name;
  if (!name) return undefined;

  const url = organizer.url || organizer.website || profileUrl;
  return {
    "@type": "Organization",
    name,
    ...(url ? { url } : {}),
  };
};

/**
 * Resolves performers (artists)
 */
const resolvePerformers = (artists) => {
  if (!artists) return undefined;
  const list = Array.isArray(artists) ? artists : [artists];

  const performers = list
    .map((a) => {
      if (!a) return null;
      const name =
        typeof a === "string"
          ? a.trim()
          : a.name || `${a.first_name || ""} ${a.last_name || ""}`.trim();
      if (!name) return null;

      const img =
        typeof a === "object"
          ? a.image?.[0]?.url || a.image || a.profile_image
          : null;
      const slug = typeof a === "object" ? a.slug : null;

      return {
        "@type": "PerformingGroup",
        name: name,
        ...(img
          ? {
            image: img.startsWith("http")
              ? img
              : `${BASE_URL}${img.startsWith("/") ? "" : "/"}${img}`,
          }
          : {}),
        ...(slug ? { url: `${BASE_URL}/artist/${slug}` } : {}),
      };
    })
    .filter(Boolean);

  return performers.length > 0 ? performers : undefined;
};

/**
 * Cleans HTML tags from a real description. Returns null when there is no
 * genuine description text — callers must omit `description` rather than
 * synthesizing generic boilerplate copy.
 */
const cleanDescription = (desc) => {
  if (desc && typeof desc === "string") {
    const text = desc
      .replace(/<[^>]*>/g, "")
      .replace(/\s+/g, " ")
      .trim();
    if (text.length > 0) {
      return text.length > 500 ? `${text.substring(0, 497)}...` : text;
    }
  }
  return null;
};

/**
 * Generates schema.org/Event JSON-LD from real event data only. Every field
 * is included only when the underlying event actually has that data —
 * missing data means an omitted field, never a fabricated placeholder.
 * Returns null when the event lacks a field Google requires for a genuine
 * Event (name, startDate, location.address) — such an "event" isn't a real
 * rich-result candidate, and faking any of them would misrepresent it.
 */
export const generateSchema = (
  name,
  image,
  start_date,
  start_time,
  end_date,
  end_time,
  timezone,
  slug,
  venue,
  description,
  price,
  sold_out,
  currency,
  max_price,
  organizer,
  status,
  extraOptions = {},
) => {
  // DEV-023: "Samay Raina Live | India Tour" — the pipe is a separator, not the name.
  const safeName =
    typeof name === "string" ? name.trim().replace(/\s*\|\s*/g, " – ") : "";
  if (!safeName) return null;

  const isoStartDate = start_date ? getISODateTime(start_date, start_time, timezone) : null;
  if (!isoStartDate) return null;

  const venueCity = typeof venue === "object" ? venue?.city : null;
  const eventUrl = getCanonicalEventUrl(slug, venueCity);
  const imageUrl = resolveAbsoluteImageUrl(image, extraOptions?.feature_images);

  const isoEndDate = end_date ? getISODateTime(end_date, end_time, timezone) : null;

  const isPast =
    String(status).toLowerCase() === "completed" ||
    String(status).toLowerCase() === "past" ||
    extraOptions?.is_past_event === true ||
    Boolean(isoEndDate && new Date(isoEndDate) < new Date()) ||
    Boolean(isoStartDate && !isoEndDate && new Date(isoStartDate) < new Date());

  const availabilityStatus = sold_out
    ? "https://schema.org/SoldOut"
    : isPast
      ? "https://schema.org/Discontinued"
      : "https://schema.org/InStock";

  // Google requires location.address on an Event. With no address at all
  // (no venue data, or a bare venue-name string) the block can't pass, so
  // none is emitted rather than one that GSC flags as invalid.
  const locationObj = resolveLocation(venue, extraOptions?.yet_to_be_announced);
  if (!locationObj.address) return null;
  // Fallback only with a real organizer name: falling back to the event's own
  // name published e.g. "Satinder Sartaaj … | MB Khalsa College" as the
  // organizer of third-party events. organizer is optional for Event.
  const organizerObj =
    resolveOrganizer(
      organizer,
      extraOptions?.organizer_slug ||
        extraOptions?.organizer_details?.workspace_slug,
    ) ||
    (extraOptions?.organizer_name
      ? { "@type": "Organization", name: extraOptions.organizer_name }
      : null);
  const performers =
    resolvePerformers(extraOptions?.artists_details || extraOptions?.artists) ||
    (extraOptions?.artist_name
      ? [{ "@type": "PerformingGroup", name: extraOptions.artist_name }]
      : undefined);
  const plainDescription =
    cleanDescription(description) ||
    `${safeName} happening at ${venue?.name || venueCity || "Tykkit"}.`;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: safeName,
    startDate: isoStartDate,
    eventStatus: `https://schema.org/${mapEventStatus(status, isoEndDate || isoStartDate)}`,
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: locationObj,
  };

  if (isoEndDate) {
    if (new Date(isoEndDate) >= new Date(isoStartDate)) {
      schema.endDate = isoEndDate;
    }
  }
  schema.image = [imageUrl || `${BASE_URL}/logo.png`];
  schema.description = plainDescription;
  if (organizerObj) schema.organizer = organizerObj;
  if (performers) schema.performer = performers;

  const lowPriceNum = parsePrice(price);
  if (lowPriceNum !== null) {
    const maxPriceNum = max_price != null ? parsePrice(max_price) : lowPriceNum;
    const highPriceNum =
      maxPriceNum !== null && maxPriceNum >= lowPriceNum ? maxPriceNum : lowPriceNum;

    schema.offers = [
      {
        "@type": "AggregateOffer",
        url: eventUrl,
        priceCurrency: currency || "INR",
        price: lowPriceNum,
        lowPrice: lowPriceNum,
        highPrice: highPriceNum,
        availability: availabilityStatus,
        validFrom: isoStartDate,
      },
    ];
  }

  return schema;
};

/**
 * Event node for nesting inside another entity's schema (an organizer's
 * `event`, an artist's `performerIn`). Built with generateSchema so it
 * matches the event page's own Event schema, from whatever a listing row
 * carries: listings send the venue as `venue`, artist events as `address`,
 * and neither includes a description. Returns null when generateSchema
 * can't build a valid Event (no name, start date or address).
 */
export const generateNestedEventSchema = (ev) => {
  if (!ev) return null;

  const venue = ev.venue || ev.address;
  const schema = generateSchema(
    ev.name,
    ev.image || ev.cover_image,
    ev.start_date,
    ev.start_time,
    ev.end_date,
    ev.end_time,
    ev.timezone,
    ev.slug,
    venue,
    ev.description,
    ev.price ?? ev.amount,
    ev.sold_out,
    ev.currency,
    ev.max_price,
    ev.organizer,
    ev.status,
    {
      feature_images: ev.feature_images || ev.banner_image,
      yet_to_be_announced: ev.yet_to_be_announced,
      organizer_slug: ev.organizer_slug || ev.workspace_slug,
    },
  );
  if (!schema) return null;

  const { "@context": _context, ...event } = schema;
  return {
    ...event,
    url: getCanonicalEventUrl(
      ev.slug,
      typeof venue === "object" ? venue?.city : null,
    ),
  };
};
